/**
 * ISR (Incremental Static Regeneration) Constants
 * 
 * These constants control how often Next.js revalidates cached pages
 * in production. Pages will be served from CDN cache until the
 * revalidation period expires.
 * 
 * ## Architecture Overview
 * 
 * Pages can be statically generated with ISR when they:
 * - Show the same content to all users (public data)
 * - Don't require auth checks for initial render
 * - Can fetch user-specific data client-side (like facepiles)
 * 
 * Pages must be dynamic when they:
 * - Require privacy/permission checks server-side
 * - Show different content based on auth state
 * - Need server-side user context for initial render
 */

/**
 * ISR revalidation intervals in seconds
 */
export const ISR_REVALIDATION = {
	/**
	 * Static Content - Long cache for rarely changing pages
	 * Use for: Legal pages, terms of service, privacy policy
	 * Revalidates: Every 24 hours
	 */
	STATIC_CONTENT: 86400, // 24 hours

	/**
	 * Detail Pages - Medium cache for content pages
	 * Use for: Festival details, show details, artist profiles
	 * Revalidates: Every 5 minutes
	 */
	DETAIL_PAGES: 300, // 5 minutes

	/**
	 * Discovery/Listing Pages - Medium cache for list views
	 * Use for: Festival listings, show listings, artist directories
	 * Revalidates: Every 5 minutes
	 */
	LISTING_PAGES: 300, // 5 minutes

	/**
	 * Support Pages - Medium cache for help content
	 * Use for: Support pages, FAQ, documentation
	 * Revalidates: Every hour
	 */
	SUPPORT_PAGES: 3600, // 1 hour
} as const;

/**
 * Number of popular items to pre-build at build time
 * for detail pages (festivals, artists, shows).
 * Kept low to avoid exhausting Supabase connection pooler
 * during concurrent static page generation. Remaining pages
 * are generated on-demand via dynamicParams + ISR.
 */
export const ISR_STATIC_GENERATION_LIMIT = 10;